/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, September 24th 2021, 10:21:05 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import * as React from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Autocomplete, { AutocompleteProps } from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { useAPIFetchList, UseFetchListArgs } from "lib/api";
import Tooltip, { TooltipProps } from "@mui/material/Tooltip";
import { useDeepEffect } from "lib/deep_hooks";
import { APIErrorWrapper } from "ui-widgets/wrappers/ErrorWrapper";

export interface AsyncAPIListAutoCompleteProps<OSObjectType>
  extends Partial<AutocompleteProps<OSObjectType, false, boolean, false>>,
    Omit<UseFetchListArgs<OSObjectType>, "makeRequestOnInitialMount"> {
  label: string;
  textFieldProps?: TextFieldProps; // Allowing one to pass down any props to the text field that is rendered by the autocomplete widget
  tooltipProps?: Partial<TooltipProps>;
  inputStartAdornment?: React.ReactNode;
  inputEndAdornment?: React.ReactNode;
  itemIdProperty: keyof OSObjectType;
  onChangeCallBack?: (selectedItem: OSObjectType) => void;
}

export default function AsyncAPIListAutoComplete<OSObjectType>({
  id,
  label,
  apiServicePath,
  apiQueryParams,
  filterByGlobalOrganization,
  defaultValue,
  itemIdProperty,
  getOptionLabel,
  loadingText,
  onChangeCallBack,
  textFieldProps,
  tooltipProps,
  inputStartAdornment = null,
  inputEndAdornment = null,
  ...props
}: AsyncAPIListAutoCompleteProps<OSObjectType>) {
  const { value, ...otherProps } = props;

  const [open, setOpen] = React.useState(false);

  const [options, setOptions] = React.useState<readonly OSObjectType[]>([]);

  const { fetchList, items, isAPIError, isMakingAPIRequest, httpResponseStatusCode } = useAPIFetchList({
    apiServicePath,
    apiQueryParams,
    filterByGlobalOrganization,
    makeRequestOnInitialMount: false,
  });

  const loading = open && isMakingAPIRequest;

  //Tracking if the component is controlled
  const { current: isControlled } = React.useRef(value !== undefined);

  //When the component is controlled
  const [controlledValue, setControlledValue] = React.useState<OSObjectType>(value as OSObjectType);

  //When the component is controlled
  React.useEffect(() => {
    isControlled && setControlledValue(value as OSObjectType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  React.useEffect(() => {
    if (!open) {
      setOptions([]);
      return;
    }

    return fetchList();
  }, [open, fetchList]);

  useDeepEffect(() => {
    setOptions(isAPIError ? [] : [...items]);
  }, [isMakingAPIRequest, isAPIError, items]);

  // Got this from the autocomplete API for the onChange prop format - https://mui.com/api/autocomplete/
  const handleChange = (
    event: React.SyntheticEvent,
    pickedItem: NonNullable<OSObjectType>,
    reason: any,
    details: any
  ): void => {
    isControlled && setControlledValue(pickedItem);
    onChangeCallBack && onChangeCallBack(pickedItem); //Only run if the function has been passed in props
  };

  const renderOptionOverride = (optionProps: object, option: OSObjectType, state: object) => {
    // Overriding the renderOption function in MUI Autocomplete to fix a possible bug. By default MUI Autocomplete
    // uses the optionLabel as the key for the renderered list items. (See getOptionProps function in MUI useAutoComplete.js)
    // The problem is, some options May have similar names resulting in similar keys. And thus when state changes e.g.
    // during a search, react will not be able to correctly pick the options changed. So here, we use the Id property as the key.
    // This is also done because theoretically, any given user's Organization names will be different but practically it should be possible to allow the opposite.

    const { key, ...optionPropsWithoutKey } = optionProps as any;

    return (
      <li key={option[itemIdProperty]} {...optionPropsWithoutKey}>
        {getOptionLabel(option)}
      </li>
    );
  };

  return (
    <>
      <APIErrorWrapper isAPIError={isAPIError} httpResponseStatusCode={httpResponseStatusCode} />

      <Autocomplete
        id={id}
        sx={otherProps.sx ?? (otherProps.fullWidth ? {} : { width: 300 })}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
        }}
        onChange={handleChange}
        isOptionEqualToValue={(option, value) => option[itemIdProperty] === value[itemIdProperty]}
        defaultValue={defaultValue}
        value={controlledValue}
        getOptionLabel={(option) => getOptionLabel(option) ?? ""}
        options={options}
        loading={loading}
        loadingText={loadingText}
        renderOption={renderOptionOverride}
        renderInput={(params) => (
          <Tooltip title={controlledValue ? getOptionLabel(controlledValue) ?? "" : ""} {...tooltipProps}>
            <TextField
              {...params}
              {...textFieldProps}
              label={label}
              InputProps={{
                ...params.InputProps,
                startAdornment: inputStartAdornment,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress size={20} /> : inputEndAdornment}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          </Tooltip>
        )}
        {...otherProps}
      />
    </>
  );
}
